/* ***************************************************************************
 * Image Compression Utility
 * Compresses images to target size using canvas-based resizing.
 * Only compresses image types (jpeg, png, gif). Skips other file types.
 *************************************************************************** */

export const MAX_IMAGE_SIZE_BYTES = 300 * 1024; // 300KB
export const MAX_CONTACT_CARD_PHOTO_SIZE_BYTES = 50 * 1024; // 50KB for vCard photos

const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

/* ***************************************************************************
 * isCompressibleImage - Check if file is a compressible image type
 *************************************************************************** */
export const isCompressibleImage = (file) => file && IMAGE_TYPES.includes(file.type);

/* ***************************************************************************
 * compressImage - Resize image iteratively until under target size
 * Returns: { success, blob?, error?, originalSize?, compressedSize? }
 * @param {File} file - The image file to compress
 * @param {number} maxSizeBytes - Target max size in bytes (default 300KB)
 * @param {string|null} outputType - Force output type (e.g., 'image/jpeg'). If null, uses original type.
 *************************************************************************** */
export const compressImage = (file, maxSizeBytes = MAX_IMAGE_SIZE_BYTES, outputType = null) => new Promise((resolve) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const img = new Image();
  const mimeType = outputType || file.type;

  img.onerror = () => {
    resolve({ success: false, error: 'Failed to load image for compression' });
  };

  img.onload = () => {
    const { width, height } = img;
    const aspectRatio = width / height;

    // Start with original dimensions
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, 0, 0);

    // Check if resize is needed
    canvas.toBlob((blob) => {
      if (!blob) {
        resolve({ success: false, error: 'Failed to process image' });
        return;
      }

      if (blob.size <= maxSizeBytes) {
        resolve({
          success: true,
          blob,
          originalSize: file.size,
          compressedSize: blob.size,
        });
        return;
      }

      // Reduce dimensions by 90% iteratively
      const reduceSize = (currentWidth, currentHeight, attempt = 1) => {
        const newWidth = Math.floor(currentWidth * 0.9);
        const newHeight = Math.floor(newWidth / aspectRatio);

        // Safety: stop if dimensions too small or too many attempts
        if (newWidth < 50 || attempt > 50) {
          resolve({
            success: false,
            error: `Image could not be compressed to under ${Math.round(maxSizeBytes / 1024)}KB. Please use a smaller image.`,
            originalSize: file.size,
          });
          return;
        }

        canvas.width = newWidth;
        canvas.height = newHeight;
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        canvas.toBlob((newBlob) => {
          if (!newBlob) {
            resolve({ success: false, error: 'Failed to process image' });
            return;
          }

          if (newBlob.size <= maxSizeBytes) {
            resolve({
              success: true,
              blob: newBlob,
              originalSize: file.size,
              compressedSize: newBlob.size,
            });
          } else {
            reduceSize(newWidth, newHeight, attempt + 1);
          }
        }, mimeType, 0.8);
      };

      reduceSize(width, height);
    }, mimeType, 0.8);
  };

  img.src = URL.createObjectURL(file);
});

/* ***************************************************************************
 * compressImageIfNeeded - Main entry point for image compression
 * - Only compresses images (jpeg, png, gif)
 * - Skips compression if already under target size
 * - Returns original file for non-image types
 * @param {File} file - The file to potentially compress
 * @param {number} maxSizeBytes - Target max size in bytes (default 300KB)
 * Returns: { success, file?, error?, wasCompressed, originalSize?, compressedSize? }
 *************************************************************************** */
export const compressImageIfNeeded = async (file, maxSizeBytes = null) => {
  // Use default 300KB if no size specified
  const targetMaxSize = maxSizeBytes || MAX_IMAGE_SIZE_BYTES;

  try {
    // Not an image - return as-is (no compression for PDFs, docs, videos, etc.)
    if (!isCompressibleImage(file)) {
      return { success: true, file, wasCompressed: false };
    }

    // Already under limit - no compression needed
    if (file.size <= targetMaxSize) {
      return { success: true, file, wasCompressed: false };
    }

    // Compress the image
    const result = await compressImage(file, targetMaxSize);

    if (!result.success) {
      console.error('Image compression failed:', result.error);
      return {
        success: false,
        error: result.error,
        originalSize: result.originalSize,
      };
    }

    // Convert blob back to File with original name
    const compressedFile = new File([result.blob], file.name, { type: file.type });

    return {
      success: true,
      file: compressedFile,
      wasCompressed: true,
      originalSize: result.originalSize,
      compressedSize: result.compressedSize,
    };
  } catch (error) {
    console.error('Image compression error:', error);
    const sizeKB = Math.round(targetMaxSize / 1024);
    return {
      success: false,
      error: `Image could not be compressed to under ${sizeKB}KB. Please use a smaller image.`,
    };
  }
};
